/**
 * Sysadmin Audit → Sysadmin Actions (Wave 3 §10.2 #7 / spec §3.6).
 *
 * platform_audit_logs view — sysadmin-only verbs. Cursor pagination
 * matches the /stream tab.
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ShieldCheck, Filter, X, RefreshCw, AlertTriangle, Clock,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  useSysadminActions,
  type SysadminActionRow,
  type SysadminActionsFilters,
} from '@/lib/use-sysadmin-audit';

export const Route = createFileRoute('/sysadmin/audit/sysadmin')({
  component: SysadminActionsPage,
});

function SysadminActionsPage() {
  const [committed, setCommitted] = useState<SysadminActionsFilters>({ limit: 50 });
  const [draft, setDraft] = useState<SysadminActionsFilters>({ limit: 50 });
  const [accumulated, setAccumulated] = useState<SysadminActionRow[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { data, isLoading, error, refetch, isFetching } =
    useSysadminActions(committed);

  useEffect(() => {
    if (!data) return;
    setAccumulated((prev) => {
      if (!committed.cursor) return data.items;
      const seen = new Set(prev.map((r) => r.cursor));
      const out = [...prev];
      for (const r of data.items) {
        if (!seen.has(r.cursor)) out.push(r);
      }
      return out;
    });
  }, [data, committed.cursor]);

  const apply = () => setCommitted({ ...draft, cursor: undefined });
  const clear = () => {
    const cleared: SysadminActionsFilters = { limit: 50 };
    setDraft(cleared);
    setCommitted(cleared);
  };
  const loadMore = () =>
    data?.nextCursor && setCommitted({ ...committed, cursor: data.nextCursor });

  const filtersDirty =
    JSON.stringify({ ...draft, cursor: undefined }) !==
    JSON.stringify({ ...committed, cursor: undefined });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Sysadmin Actions
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.6</Badge>
              <Badge variant="secondary" className="text-xs">v1</Badge>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setCommitted({ ...committed, cursor: undefined });
            refetch();
          }}
          disabled={isFetching}
        >
          <RefreshCw className={isFetching ? 'h-3.5 w-3.5 mr-1.5 animate-spin' : 'h-3.5 w-3.5 mr-1.5'} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
          <CardDescription>
            <code>platform_audit_logs</code> — every action by every
            sysadmin. Includes impersonation sessions, key rotations,
            tenant lifecycle, model promotions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="f-sysadmin" className="text-xs">Sysadmin ID</Label>
              <Input
                id="f-sysadmin"
                value={draft.sysadminId ?? ''}
                onChange={(e) => setDraft({ ...draft, sysadminId: e.target.value || undefined })}
                placeholder="UUID"
                className="font-mono text-xs h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-action" className="text-xs">Action</Label>
              <Input
                id="f-action"
                value={draft.action ?? ''}
                onChange={(e) => setDraft({ ...draft, action: e.target.value || undefined })}
                placeholder="PLATFORM_TENANT_CREATED"
                className="font-mono text-xs h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-outcome" className="text-xs">Outcome</Label>
              <Select
                value={draft.outcome ?? '__any__'}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    outcome: v === '__any__' ? undefined : (v as 'success' | 'failure'),
                  })
                }
              >
                <SelectTrigger id="f-outcome" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__" className="text-xs">Any</SelectItem>
                  <SelectItem value="success" className="text-xs">Success</SelectItem>
                  <SelectItem value="failure" className="text-xs">Failure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-target" className="text-xs">Target tenant</Label>
              <Input
                id="f-target"
                value={draft.targetTenantId ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, targetTenantId: e.target.value || undefined })
                }
                placeholder="UUID"
                className="font-mono text-xs h-8"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            {filtersDirty && (
              <Button variant="ghost" size="sm" onClick={() => setDraft(committed)}>
                Discard
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={clear}>
              <X className="h-3.5 w-3.5 mr-1" />
              Clear
            </Button>
            <Button size="sm" onClick={apply} disabled={!filtersDirty}>
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Body */}
      {isLoading && accumulated.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Loading sysadmin actions…
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {error.message}</div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Actions</CardTitle>
            <CardDescription>
              Showing {accumulated.length} action
              {accumulated.length === 1 ? '' : 's'}.
              {data?.nextCursor ? ' More available.' : ' End of stream.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            {accumulated.length === 0 ? (
              <div className="px-6 py-8 text-sm text-muted-foreground text-center">
                No actions match the current filters.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>When</TableHead>
                    <TableHead>Sysadmin</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Target tenant</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Outcome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accumulated.map((r) => {
                    const isOpen = expanded.has(r.id);
                    // #1125 — every high-risk row carries forensic metadata
                    // (reason / sessionId / requestId / stepUpEvidence) in
                    // the API response. Expand-row drawer keeps the table
                    // compact while making the data discoverable.
                    const hasForensic =
                      !!r.reason ||
                      !!r.sessionId ||
                      !!r.requestId ||
                      !!r.stepUpEvidence ||
                      !!r.userAgent ||
                      (r.details && Object.keys(r.details).length > 0);
                    return (
                      <>
                        <TableRow key={r.id}>
                          <TableCell className="w-8 align-top">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => toggleExpanded(r.id)}
                              disabled={!hasForensic}
                              aria-label={isOpen ? 'Collapse details' : 'Expand details'}
                              aria-expanded={isOpen}
                              title={hasForensic ? 'Show forensic details' : 'No additional details'}
                            >
                              {isOpen ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(r.createdAt).toLocaleString()}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">
                            <div className="font-medium truncate max-w-[180px]" title={r.sysadminEmail}>
                              {r.sysadminEmail}
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{r.action}</code>
                            <Badge variant="outline" className="ml-1 text-[10px]">{r.category}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {r.targetTenantName ? (
                              <div className="truncate max-w-[160px]" title={r.targetTenantName}>
                                {r.targetTenantName}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {r.targetResourceType ? (
                              <span className="font-mono">{r.targetResourceType}</span>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {r.reason ? (
                              <span className="truncate max-w-[220px] block" title={r.reason}>
                                {r.reason}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            <Badge variant={r.outcome === 'success' ? 'default' : 'destructive'}>
                              {r.outcome}
                            </Badge>
                          </TableCell>
                        </TableRow>
                        {isOpen && hasForensic && (
                          <TableRow key={`${r.id}-details`} className="bg-muted/30">
                            <TableCell />
                            <TableCell colSpan={7} className="text-xs space-y-1.5 py-3">
                              {r.sessionId && (
                                <div>
                                  <span className="text-muted-foreground mr-2">Session:</span>
                                  <code className="font-mono">{r.sessionId}</code>
                                </div>
                              )}
                              {r.requestId && (
                                <div>
                                  <span className="text-muted-foreground mr-2">Request:</span>
                                  <code className="font-mono">{r.requestId}</code>
                                </div>
                              )}
                              {r.stepUpEvidence && (
                                <div>
                                  <span className="text-muted-foreground mr-2">Step-up:</span>
                                  <code className="font-mono">{r.stepUpEvidence}</code>
                                </div>
                              )}
                              {r.userAgent && (
                                <div>
                                  <span className="text-muted-foreground mr-2">UA:</span>
                                  <span className="font-mono break-all">{r.userAgent}</span>
                                </div>
                              )}
                              {r.details && Object.keys(r.details).length > 0 && (
                                <div>
                                  <div className="text-muted-foreground mb-1">Details (redacted):</div>
                                  <pre className="font-mono text-[11px] bg-background border rounded px-2 py-1 overflow-x-auto">
{JSON.stringify(r.details, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {data?.nextCursor && (
              <div className="flex justify-center py-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMore}
                  disabled={isFetching}
                >
                  {isFetching ? 'Loading…' : 'Load 50 more'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
